

'use client';

import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { CheckCircle, Zap, Palette, Users, Shield, Search, Sparkles, LineChart, Bot, Image as ImageIcon, SlidersHorizontal, Share2, PlusCircle, Rocket, FileUp, Edit, FileText, Layers, ArrowLeftRight, Check, Rss, Heart, Gift, AppWindow, Eye, Gamepad2, ShoppingCart, Ruler, Paintbrush, Library, BookText, Paperclip, Mail, Tag, MailOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Metadata } from 'next';

const recentlyAddedFeatures = [
     {
        icon: Tag,
        title: 'Artist Characterization',
        description: 'Characterize artists by their roles, such as Painter, Photographer, Sculptor, or Draftsman, to allow for better discovery and filtering.',
        status: 'Added',
        badgeColor: 'bg-green-100 text-green-800',
        addedDate: "October 2, 2024",
    },
     {
        icon: Heart,
        title: 'Follow Artists',
        description: 'Follow your favorite artists and you will get notified if there is new artwork added.',
        status: "Live",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "September 14, 2024",
    },
     {
        icon: Users,
        title: 'Frequent Collaborators',
        description: 'See a list of artists who frequently collaborate with the artist you are viewing, making it easy to discover artistic connections.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "September 13, 2024",
    },
    {
        icon: Paperclip,
        title: 'Private Images',
        description: 'Allow the ability to add private images to an item, visible only to you, for storing receipts or certificates.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "September 13, 2024",
    },
    {
        icon: Share2,
        title: 'Artist Social Media Links',
        description: 'Add and display links to artist social media profiles like Instagram, Twitter, etc.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "September 13, 2024",
    },
    {
        icon: Gamepad2,
        title: 'Guess the Artist Game',
        description: 'A daily game where you try to guess the artist of a featured artwork.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "August 8, 2025",
    },
    {
        icon: ImageIcon,
        title: 'Add/Edit Artwork Images',
        description: 'Ability to add, remove, or reorder images for an artwork after it has been created, not just during the initial upload.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "June 27, 2025",
    },
    {
        icon: ArrowLeftRight,
        title: 'Personal Data Portability',
        description: 'You can now export your entire collection, including private notes and purchase details, to a CSV file. You can also import updates from a CSV.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "June 26, 2025",
    },
    {
        icon: Layers,
        title: 'Curated Artwork Sets',
        description: 'Group artworks into curated public sets, creating thematic collections and digital exhibitions for everyone to explore.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800",
        addedDate: "June 24, 2025",
    },
    {
        icon: Edit,
        title: 'Suggest Edits to Artists',
        description: 'Help keep artist profiles accurate and up-to-date by suggesting edits to their biography, aliases, or links.',
        status: 'Added',
        badgeColor: 'bg-green-100 text-green-800'
    },
    {
        icon: Edit,
        title: 'Suggest Edits to Artworks',
        description: 'Contribute to the community by suggesting corrections or adding details to publicly visible artworks.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800"
    },
    {
        icon: FileText,
        title: 'PDF Collection Export',
        description: 'Download a beautifully formatted PDF of your entire collection, perfect for insurance, appraisal, or personal records.',
        status: "Added",
        badgeColor: "bg-green-100 text-green-800"
    },
];

const coreFeatures = [
    {
        icon: ImageIcon,
        title: 'Multiple Artwork Images',
        description: 'You can now add up to five images per artwork, perfect for capturing details like signatures, framing, or different angles.',
    },
    {
        icon: SlidersHorizontal,
        title: 'Advanced Collection Management',
        description: 'Your personal collection is now searchable and sortable by date added, title, or artist name, making it easier to find exactly what you\'re looking for.',
    },
     {
        icon: Users,
        title: 'Artwork Collaborators',
        description: 'Assign multiple artists to a single artwork to accurately represent collaborative pieces.',
    },
    {
        icon: Palette,
        title: 'Catalog Your Collection',
        description: 'Easily add and organize every piece in your art collection. Keep track of titles, artists, descriptions, dimensions, purchase history, and more.',
    },
    {
        icon: Search,
        title: 'Discover New Art',
        description: 'Explore a growing gallery of public artworks shared by other collectors and artists on Art Vault. Find inspiration and connect with the community.',
    },
    {
        icon: Share2,
        title: 'Share Your Passion',
        description: 'Choose to make your artworks public to share with the community, or keep them private in your personal vault. You control what\'s visible.',
    },
    {
        icon: PlusCircle,
        title: 'Seamless Artist Creation',
        description: 'Can\'t find an artist? No problem. Add a new artist directly when you upload their artwork, helping to grow the platform\'s database.',
    },
    {
        icon: Rocket,
        title: 'Simple & Beautiful',
        description: 'Enjoy a clean, intuitive, and aesthetically pleasing interface designed to make managing and appreciating your art a delight.',
    }
];

const comingSoonFeatures = [
     {
        icon: Mail,
        title: 'New Artwork Notifications',
        description: 'Get notified when artists you follow add new artwork. Choose between daily, weekly, or monthly email digests based on your preferences.',
        status: 'Planned',
        badgeColor: 'bg-blue-100 text-blue-800'
    },
    {
        icon: Search,
        title: 'Global Search',
        description: 'Search for artworks, artists, and more from anywhere in the app.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Search,
        title: 'Search Artists',
        description: 'Quickly find artists on the artists page with a new search bar.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: ShoppingCart,
        title: 'Buy Now Filter',
        description: 'Ability to search & discover artworks that are available for purchase directly.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Ruler,
        title: 'Search by Size',
        description: 'Filter artworks based on their dimensions to find the perfect piece for your space.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Paintbrush,
        title: 'Search by Medium',
        description: 'Filter artworks by their medium, such as sculpture, skateboard, print, etc.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Library,
        title: 'Gallery & Events List',
        description: 'A curated directory of art galleries and a calendar of upcoming art-related events and exhibitions.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: ArrowLeftRight,
        title: 'Exchange Rates API',
        description: 'Convert artwork prices into your local currency using real-time exchange rates.',
        status: 'Planned',
        badgeColor: 'bg-blue-100 text-blue-800'
    },
    {
        icon: FileUp,
        title: 'Bulk CSV Import',
        description: 'Bulk import artworks from a standard CSV or a Shopify products export, making it easy to populate the gallery.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Heart,
        title: 'Artwork Wishlist',
        description: 'You can add artworks to your wishlist.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
     {
        icon: AppWindow,
        title: 'Dedicated Mobile Apps',
        description: 'Develop native iOS and Android applications for an optimized mobile experience to manage and view your collection on the go.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Rss,
        title: 'RSS Feed Integration',
        description: 'Subscribe to RSS feeds for new articles, news, or updates for specific artists or new public artworks, keeping you informed directly in your feed reader.',
        status: "Planned",
        badgeColor: "bg-blue-100 text-blue-800"
    },
    {
        icon: Sparkles,
        title: 'AI-Powered Tagging',
        description: 'Automatically tag your uploaded artwork with relevant keywords and categories using generative AI.',
        status: "Exploring",
        badgeColor: "bg-purple-100 text-purple-800"
    },
    {
        icon: Rss,
        title: 'Instagram Graph API Integration',
        description: 'Automatically pull in posts from an artist\'s Instagram page to enrich their profile.',
        status: "Exploring",
        badgeColor: "bg-purple-100 text-purple-800"
    },
     {
        icon: BookText,
        title: 'Wikipedia API Integration',
        description: 'Automatically pull in artist biographies and other relevant data from Wikipedia to enrich artist profiles.',
        status: "Exploring",
        badgeColor: "bg-purple-100 text-purple-800"
    },
    {
        icon: Bot,
        title: 'AI Art Advisor',
        description: 'Get recommendations and insights about your collection from an AI-powered art advisor.',
        status: "Exploring",
        badgeColor: "bg-purple-100 text-purple-800"
    },
    {
        icon: Gift,
        title: 'Referral Program',
        description: 'Every member you refer who becomes a paying member, you get one month free.',
        status: "Future",
        badgeColor: "bg-gray-100 text-gray-800"
    },
    {
        icon: LineChart,
        title: 'Collection Analytics',
        description: 'Visualize your collection\'s value over time and gain insights into your collecting habits.',
        status: "Future",
        badgeColor: "bg-gray-100 text-gray-800"
    },
    {
        icon: LineChart,
        title: 'StockX API Integration',
        description: 'Get average price or pricing of items by integrating with the StockX API.',
        status: "Future",
        badgeColor: "bg-gray-100 text-gray-800"
    },
     {
        icon: LineChart,
        title: 'Google Trends API Integration',
        description: 'Track artist popularity and search interest over time to identify trends.',
        status: "Future",
        badgeColor: "bg-gray-100 text-gray-800"
    },
    {
        icon: LineChart,
        title: 'Automated eBay Sale Tracking',
        description: 'Automatically track the sale status and final price of artworks listed on eBay by simply adding the URL to an item.',
        status: "Future",
        badgeColor: "bg-gray-100 text-gray-800"
    },
];


export default function FeaturesPage() {
    return (
        <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-headline mb-2">Features & Roadmap</h1>
                        <p className="text-xl text-muted-foreground">What you can do with Art Vault today, and what's coming next.</p>
                    </div>

                    <div className="space-y-12">
                        <div>
                            <h2 className="text-3xl font-headline mb-8 text-center">Recently Added</h2>
                            <div className="space-y-4">
                                {recentlyAddedFeatures.map((feature, index) => (
                                     <div key={index} className="bg-card border rounded-lg overflow-hidden">
                                        <div className="p-4 flex justify-between items-start gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="bg-green-100 text-green-800 p-3 rounded-lg">
                                                    <feature.icon className="h-6 w-6" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold">{feature.title}</h3>
                                                </div>
                                            </div>
                                            <Badge className={cn("text-xs font-bold", feature.badgeColor)}>
                                                <Check className="mr-1 h-3 w-3" />
                                                {feature.status}
                                            </Badge>
                                        </div>
                                        <div className="px-4 pb-4 pl-16 flex justify-between items-end">
                                             <p className="text-muted-foreground text-sm max-w-xl">{feature.description}</p>
                                             {feature.addedDate && (
                                                <p className="text-xs text-muted-foreground/80 font-mono">{feature.addedDate}</p>
                                             )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h2 className="text-3xl font-headline mb-8 text-center">Core Features</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {coreFeatures.map((feature, index) => (
                                    <div key={index} className="bg-card p-6 rounded-lg border flex items-start gap-4">
                                        <div className="bg-primary/10 text-primary p-3 rounded-full">
                                            <feature.icon className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold mb-1">{feature.title}</h3>
                                            <p className="text-muted-foreground text-sm">{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h2 className="text-3xl font-headline mb-8 text-center">Coming Soon</h2>
                             <div className="space-y-4">
                                {comingSoonFeatures.map((feature, index) => (
                                    <div key={index} className="bg-card border rounded-lg overflow-hidden">
                                        <div className="p-4 flex justify-between items-start gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="bg-muted text-muted-foreground p-3 rounded-lg">
                                                    <feature.icon className="h-6 w-6" />
                                                </div>
                                                <h3 className="text-lg font-bold">{feature.title}</h3>
                                            </div>
                                            <Badge className={cn("text-xs font-bold", feature.badgeColor)}>
                                                {feature.status}
                                            </Badge>
                                        </div>
                                        <div className="px-4 pb-4 pl-16">
                                             <p className="text-muted-foreground text-sm">{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

    

    



    




